import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import {
	Deal,
	PaymentScheduleType,
	PaymentScheduleTypeMap,
	ProductSubscription,
	SuccessFeeType,
	SuccessFeeTypeMap,
} from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import {
	CerebroCheckbox,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { Field, Formik, FormikErrors } from 'formik';
import { FormikUtils, NumberUtils } from '../../shared/utils';
import { Grid, Skeleton } from '@mui/material';
import { QueryKeys, useExcludedLendersQuery, useReducedFeeLendersQuery, useStatesQuery } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useParams } from 'react-router-dom';
import { LenderListDisplay } from './LenderListDisplay';
import { MaxCurrencyLength } from '../../shared/utils/platform';
import { getApiResponseData } from '../../shared/utils/axios';

interface UpdateDealSubscriptionDrawerProps {
	onClose: () => void;
	subscription: ProductSubscription | null;
	deal: Deal;
}

export const UpdateDealSubscriptionDrawer: React.FC<UpdateDealSubscriptionDrawerProps> = ({
	onClose,
	subscription,
	deal,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);
	const isDirectIntro = subscription?.product.isDirectIntroProduct ?? false;

	const { guid } = useParams<'guid'>();
	const excludedLendersQuery = useExcludedLendersQuery(guid);
	const reducedFeeLendersQuery = useReducedFeeLendersQuery(guid);
	const statesQuery = useStatesQuery();

	const excludedCountDefault = deal.excludedLenderCount.toString();
	const reducedCountDefault = deal.reducedFeeLenderCount.toString();

	const canUseCustomSchedule =
		subscription?.subscriptionFrequency === 'OneTime' || subscription?.subscriptionFrequency === 'Annually';

	const updateSubscriptionMutation = useMutation(
		async ({ values }: UpdateSubscriptionMutationVariables) => {
			const setup = (NumberUtils.ParseDecimal(values.setupFee, undefined) ?? 0).toString();
			const cancellation = (NumberUtils.ParseDecimal(values.cancellationFee, undefined) ?? 0).toString();
			const reducedFee = (
				NumberUtils.ParseDecimal(values.reducedFeeLenderDiscountPercent, undefined) ?? 0
			).toString();
			const minimumFee = (NumberUtils.ParseDecimal(values.minimumSuccessFee, undefined) ?? 0).toString();
			const successFeeCap = (NumberUtils.ParseDecimal(values.successFeeCap, undefined) ?? 0).toString();

			let formData = new FormData();
			formData.append('term', values.term);
			formData.append('setupFee', setup);
			formData.append('cancellationFee', cancellation);
			formData.append('successFeeType', values.successFeeType);
			formData.append('basisPoints', values.basisPoints);
			formData.append('reducedFeeLenderDiscountPercent', reducedFee);
			formData.append('minimumSuccessFee', values.successFeeType == 'None' ? '0' : minimumFee);

			formData.append('companyName', values.companyName);
			formData.append('city', values.city);
			formData.append('postalCode', values.postalCode);
			formData.append('state', values.state);
			formData.append('address1', values.address1);
			formData.append('platformAccessPaymentSchedule', values.platformAccessPaymentSchedule);
			formData.append('successFeePaymentSchedule', values.successFeePaymentSchedule);
			formData.append('feesCredited', values.feesCredited ? 'true' : 'false');
			formData.append('successFeeCap', successFeeCap);
			formData.append('includeSbaExclusion', values.includeSbaExclusion ? 'true' : 'false');
			formData.append('excludedLendersCount', values.excludedLendersCount.toString());
			formData.append('reducedFeeLendersCount', values.reducedFeeLendersCount.toString());
			formData.append('tailPeriodMonths', values.tailPeriodMonths.toString());

			const { data, error } = await getApiResponseData<ProductSubscription>(
				api.patch(CerebroApiEndpoints.Deals.SpecificSubscription(deal.guid, subscription?.guid), formData)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(deal.guid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(deal.guid));
				client.invalidateQueries(QueryKeys.Deal.Root(deal.guid));
				snackbar.enqueueSnackbar('Deal engagement details successfully updated.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	var validateForm = (values: UpdateSubscriptionFormValues): UpdateSubscriptionFormErrors => {
		const errors: UpdateSubscriptionFormErrors = {};

		return errors;
	};

	return (
		<CerebroDrawer ref={ref} open={subscription != null} onClose={onClose}>
			<CerebroHeading>Update Engagement Details</CerebroHeading>
			<Formik
				initialValues={{
					setupFee: NumberUtils.FormatDecimal(subscription?.setupFee),
					cancellationFee: NumberUtils.FormatDecimal(subscription?.cancellationFee),
					successFeeType: subscription?.successFeeType ?? 'None',
					term: NumberUtils.FormatDecimal(subscription?.term),
					basisPoints: NumberUtils.FormatDecimal(subscription?.basisPoints),
					reducedFeeLenderDiscountPercent: NumberUtils.FormatDecimal(
						subscription?.reducedFeeLenderDiscountPercent
					),
					companyName: subscription?.companyName ?? '',
					city: deal?.city ?? '',
					postalCode: deal?.postalCode ?? '',
					state: deal?.state?.code ?? '',
					address1: deal?.address1 ?? '',
					platformAccessPaymentSchedule: subscription?.platformAccessPaymentSchedule ?? 'Now',
					successFeePaymentSchedule: subscription?.successFeePaymentSchedule ?? 'Net7',
					feesCredited: subscription?.feesCredited ?? false,
					minimumSuccessFee: NumberUtils.FormatDecimal(subscription?.minimumSuccessFee),
					successFeeCap: NumberUtils.FormatDecimal(subscription?.successFeeCap),
					includeSbaExclusion: subscription?.includeSbaExclusion ?? false,
					excludedLendersCount: NumberUtils.FormatDecimal(
						subscription?.excludedLendersCount ?? excludedCountDefault
					),
					reducedFeeLendersCount: NumberUtils.FormatDecimal(
						subscription?.reducedFeeLendersCount ?? reducedCountDefault
					),
					tailPeriodMonths: NumberUtils.FormatDecimal(subscription?.tailPeriodMonths ?? '12'),
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(updateSubscriptionMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						{excludedLendersQuery.data && excludedLendersQuery.data.length > 0 && (
							<LenderListDisplay lenderType="Excluded" memberList={excludedLendersQuery.data} />
						)}

						{reducedFeeLendersQuery.data && reducedFeeLendersQuery.data.length > 0 && (
							<LenderListDisplay lenderType="Reduced Fee" memberList={reducedFeeLendersQuery.data} />
						)}

						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel
									id="companyName"
									hint="Optional, will be included in contract and on invoices if provided"
								>
									Company Name
								</CerebroLabel>
								<Field as={CerebroInput} id="companyName" name="companyName" disabled={isSubmitting} />
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel hint="Optional, will be included in contract and on invoices if provided">
									Company Address
								</CerebroLabel>
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel id="address1">Street</CerebroLabel>
								<Field as={CerebroInput} id="address1" name="address1" disabled={isSubmitting} />
							</Grid>

							<Grid item xs={5}>
								<CerebroLabel id="city">City</CerebroLabel>
								<Field as={CerebroInput} id="city" name="city" disabled={isSubmitting} />
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel id="state">State</CerebroLabel>
								{deal && statesQuery.isSuccess ? (
									<Field as={CerebroDropdown} name="state" disabled={isSubmitting}>
										{statesQuery.data.map(state => (
											<CerebroDropdownMenuItem key={state.code} value={state.code}>
												{state.name}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							<Grid item xs={5}>
								<CerebroLabel id="city">Zip Code</CerebroLabel>
								{deal && statesQuery.isSuccess ? (
									<Field
										as={CerebroInput}
										name="postalCode"
										disabled={isSubmitting}
										placeholder="Zip Code"
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							{!isDirectIntro && (
								<Grid item xs={6}>
									<CerebroLabel required id="setupFee">
										Platform Access Fee
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="setupFee"
										name="setupFee"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							{!isDirectIntro && (
								<Grid item xs={5}>
									<CerebroLabel id="platformAccessPaymentSchedule">Payment Terms</CerebroLabel>
									<Field
										id="platformAccessPaymentSchedule"
										as={CerebroDropdown}
										name="platformAccessPaymentSchedule"
										disabled={isSubmitting}
									>
										{Object.entries(PaymentScheduleTypeMap)
											.filter(([value]) => value !== 'Custom' || canUseCustomSchedule)
											.map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
									</Field>
								</Grid>
							)}

							<Grid item xs={11}>
								<CerebroLabel id="successFeeType">Success Fee Type</CerebroLabel>
								<Field
									id="successFeeType"
									as={CerebroDropdown}
									name="successFeeType"
									disabled={isSubmitting}
								>
									{Object.entries(SuccessFeeTypeMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Grid>

							{values.successFeeType !== 'None' && (
								<Grid item xs={11}>
									<CerebroLabel id="successFeePaymentSchedule">
										Success Fee Payment Terms
									</CerebroLabel>
									<Field
										id="successFeePaymentSchedule"
										as={CerebroDropdown}
										name="successFeePaymentSchedule"
										disabled={isSubmitting}
									>
										{Object.entries(PaymentScheduleTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>
							)}

							{subscription?.subscriptionFrequency == 'OneTime' && (
								<Grid item xs={11}>
									<CerebroLabel required id="term">
										Term
									</CerebroLabel>
									<Field as={CerebroMaskedInput} id="term" name="term" disabled={isSubmitting} />
								</Grid>
							)}

							{values.successFeeType == 'Flat' && (
								<Grid item xs={11}>
									<CerebroLabel id="basisPoints">Basis Points</CerebroLabel>
									<Field
										as={CerebroMaskedInput}
										id="basisPoints"
										name="basisPoints"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							{values.successFeeType !== 'None' && (
								<Grid item xs={5}>
									<CerebroLabel id="minimumSuccessFee">Minimum Success Fee</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="minimumSuccessFee"
										name="minimumSuccessFee"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							{values.successFeeType != 'None' && (
								<>
									<Grid item xs={11}>
										<CerebroLabel id="successFeeCap">Success Fee Cap</CerebroLabel>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="successFeeCap"
											name="successFeeCap"
											disabled={isSubmitting}
										/>
									</Grid>

									{!isDirectIntro && (
										<Grid item xs={11}>
											<CerebroLabel required id="feesCredited">
												Are platform access fees credited against success fee?
											</CerebroLabel>
											<Field
												as={CerebroCheckbox}
												id="feesCredited"
												name="feesCredited"
												disabled={isSubmitting}
											/>
										</Grid>
									)}

									<Grid item xs={11}>
										<CerebroLabel id="includeSbaExclusion">Include SBA Exclusion?</CerebroLabel>
										<Field
											as={CerebroCheckbox}
											id="includeSbaExclusion"
											name="includeSbaExclusion"
											disabled={isSubmitting}
										/>
									</Grid>
								</>
							)}

							{!isDirectIntro && (
								<Grid item xs={11}>
									<CerebroLabel required id="cancellationFee">
										Cancellation Fee
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="cancellationFee"
										name="cancellationFee"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							{!isDirectIntro && (
								<Grid item xs={11}>
									<CerebroLabel required id="excludedLendersCount">
										Excluded Lenders Count
									</CerebroLabel>
									<Field
										as={CerebroMaskedInput}
										id="excludedLendersCount"
										name="excludedLendersCount"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							{!isDirectIntro && (
								<Grid item xs={values.successFeeType != 'None' ? 6 : 11}>
									<CerebroLabel required id="reducedFeeLendersCount">
										Reduced Fee Lenders Count
									</CerebroLabel>
									<Field
										as={CerebroMaskedInput}
										id="reducedFeeLendersCount"
										name="reducedFeeLendersCount"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							{values.successFeeType != 'None' && !isDirectIntro && (
								<>
									<Grid item xs={5}>
										<CerebroLabel id="reducedFeeLenderDiscountPercent">
											Success Fee Discount for Reduced Fee Lenders
										</CerebroLabel>
										<Field
											as={CerebroMaskedInput}
											formatas="percent"
											id="reducedFeeLenderDiscountPercent"
											name="reducedFeeLenderDiscountPercent"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid(
												'reducedFeeLenderDiscountPercent',
												errors,
												touched
											)}
										/>
										<CerebroText variant="hint">
											When deal closed with a reduced fee lender this is the percentage discount
											the borrower will pay on success fee.
										</CerebroText>
										{FormikUtils.IsInvalid('reducedFeeLenderDiscountPercent', errors, touched) && (
											<CerebroText variant="error">
												{errors.reducedFeeLenderDiscountPercent}
											</CerebroText>
										)}
									</Grid>
								</>
							)}
							<Grid item xs={11}>
								<CerebroLabel required id="tailPeriodMonths">
									Tail Period (months)
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="tailPeriodMonths"
									name="tailPeriodMonths"
									disabled={isSubmitting}
								/>
							</Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type UpdateSubscriptionFormErrors = FormikErrors<UpdateSubscriptionFormValues>;
type UpdateSubscriptionMutationVariables = FormikUtils.MutateOnSubmitVariables<UpdateSubscriptionFormValues>;
interface UpdateSubscriptionFormValues {
	setupFee: string;
	term: string;
	cancellationFee: string;
	successFeeType: SuccessFeeType;
	basisPoints: string;
	reducedFeeLenderDiscountPercent: string;
	minimumSuccessFee: string;
	companyName: string;
	city: string;
	state: string;
	address1: string;
	postalCode: string;
	platformAccessPaymentSchedule: PaymentScheduleType;
	successFeePaymentSchedule: PaymentScheduleType;
	feesCredited: boolean;
	successFeeCap: string;
	includeSbaExclusion: boolean;
	excludedLendersCount: string;
	reducedFeeLendersCount: string;
	tailPeriodMonths: string;
}
